import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Alert, CircularProgress, Snackbar } from "@mui/material";
import { getInvitationInfo, login } from "../../services/auth";
import { completeRegistration } from "../../services/employee";
import { useAuth } from "../../context/AuthContext";

export default function CompleteRegistration() {
    const navigate = useNavigate();
    const location = useLocation();
    const { token } = useParams();

    const { setUserToken, setUserData, setInstitutionData } = useAuth();

    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", type: "success" });
    const isPasswordInvalid = formData.password.length > 0 && formData.password.length < 9;

    const [formData, setFormData] = useState({
        name: "",
        password: "",
        email: "",
        institutionName: "",
        institutionLogo: ""
    });

    const firstInputRef = useRef(null);

    useEffect(() => {
        if (firstInputRef.current) firstInputRef.current.focus();
    }, [loading]);

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const showSnackbar = (message, type = "success") => {
        setSnackbar({ open: true, message, type });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name || !formData.password) {
            showSnackbar("يرجى إدخال الاسم وكلمة المرور", "error");
            return;
        }

        try {
            setIsSubmitting(true);
            await completeRegistration(formData.email, formData.name, formData.password);

            const res = await login(formData.email, formData.password);
            const userData = res.data.user;
            userData.email = res.data.email;
            userData.role = res.data.user.roles[0];

            setUserData(userData);
            setUserToken(res.data.accessToken);
            setInstitutionData(res.data.institution);

            showSnackbar("تم التسجيل وتسجيل الدخول بنجاح");
            setTimeout(() => navigate(`/${userData.role}`), 1500);
        } catch (error) {
            showSnackbar("حدث خطأ أثناء إكمال التسجيل", "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await getInvitationInfo(token);
                setFormData((prev) => ({
                    ...prev,
                    email: response.email,
                    institutionName: response.institution.name,
                    institutionLogo: response.institution.logo
                }));
            } catch (err) {
                console.log(err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [location, navigate]);

    if (loading) return <div className="page"><CircularProgress /></div>;

    return (
        <div className="page">
            <div className="content-view" style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div className="card" style={{ maxWidth: 400, width: "100%", textAlign: "center" }}>
                    {formData.institutionLogo && (
                        <img
                            src={formData.institutionLogo}
                            alt="Institution Logo"
                            style={{ width: 100, margin: "0 auto 20px", borderRadius: 10 }}
                        />
                    )}

                    <h2>إكمال التسجيل</h2>

                    {/* Read-only info */}
                    <div className="read-only-info" style={{
                        marginBottom: "20px",
                        textAlign: "right",
                        backgroundColor: "#f9f9f9",
                        padding: "15px",
                        borderRadius: "8px",
                        border: "1px solid #ddd"
                    }}>
                        <div style={{ marginBottom: "10px" }}>
                            <span style={{ fontWeight: "bold", color: "#555" }}>البريد الإلكتروني:</span><br />
                            <span style={{ fontSize: "14px", color: "#333" }}>{formData.email}</span>
                        </div>
                        <div>
                            <span style={{ fontWeight: "bold", color: "#555" }}>اسم المؤسسة:</span><br />
                            <span style={{ fontSize: "14px", color: "#333" }}>{formData.institutionName}</span>
                        </div>
                    </div>

                    {/* Input fields */}
                    <form onSubmit={handleSubmit}>
                        <div className="form-group">
                            <label htmlFor="name">الاسم الكامل</label>
                            <input
                                id="name"
                                type="text"
                                name="fullName"
                                ref={firstInputRef}
                                autoComplete="name"
                                placeholder="أدخل الاسم الكامل"
                                value={formData.name}
                                onChange={(e) => handleChange("name", e.target.value)}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="password">كلمة المرور</label>
                            <input
                                id="password"
                                type="password"
                                placeholder="********"
                                value={formData.password}
                                onChange={(e) => handleChange("password", e.target.value)}
                                style={{
                                    borderColor: isPasswordInvalid ? "red" : undefined,
                                }}
                            />
                            {isPasswordInvalid && (
                                <div style={{ color: "red", fontSize: "13px", marginTop: "5px" }}>
                                    كلمة المرور يجب أن تكون على الأقل 9 خانات
                                </div>
                            )}
                        </div>

                        <button
                            className="btn filled"
                            type="submit"
                            disabled={isSubmitting}
                            style={{ width: "100%", padding: "10px 0", fontSize: "15px" }}
                        >
                            {isSubmitting ? "جاري التسجيل..." : "إكمال التسجيل"}
                        </button>
                    </form>
                </div>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                >
                    <Alert
                        onClose={() => setSnackbar({ ...snackbar, open: false })}
                        severity={snackbar.type}
                        variant="filled"
                        sx={{ width: "100%" }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </div>
        </div>
    );
}
